// Back-outs / net scope geometry — Takeoff Canvas 2.0 Track D, first slice
// (docs/implementation/TAKEOFF_CANVAS_2_COMPATIBILITY_AUDIT.md §5–§7).
//
// Pure functions over the world-feet {x, y} point convention shared by
// TakeoffRooms.PolygonJson and TakeoffConditionsDrawn.GeometryJson. No DB,
// no React, no dependency — the repo carries no polygon-clipping library and
// AREA METRICS do not need one (see below). Producing the net GEOMETRY
// itself (a polygon with holes for the canvas to render) is deferred until a
// renderer needs it; a back-out is scope geometry ("this finish is not
// applied here"), never a hole in the room — TakeoffRooms.PolygonJson is
// not touched by anything in this module.
//
// Quantity presentation standard (UI brief §8):
//     Gross − Deductions = Calculated Net  ± Override = Pricing quantity
// This module produces Gross, each deduction and Calculated Net. The
// override stays on the owning record's existing field
// (RoomElements.OverrideQty / TakeoffConditionsDrawn.QuantityOverride) and is
// composed by that record's resolver — no second override store.
//
// Rules the numbers follow ("never invent"):
//   - a back-out is deducted only where it lies INSIDE the outer scope
//     polygon; the part outside is reported (`deduct_outside_room`) and not
//     deducted;
//   - overlapping back-outs are deducted ONCE (the union), and the overlap
//     is reported (`deducts_overlap`) so nothing is double-counted silently;
//   - a degenerate ring (< 3 points or zero area) is skipped and reported.
//
// Exactness: every area is a vertical slab sweep whose boundaries are every
// vertex x AND every x where two edges cross (any polygon, not only this
// one's), so no edge crossing happens inside a slab and each slab's interior
// interval endpoints are linear in x — the midpoint length × width is the
// exact integral. Union / intersection are then plain interval operations
// per slab. Exact for any simple polygons (rectilinear or diagonal); the
// same technique polygonOverlapExtents() uses for room conflicts, extended
// with crossing boundaries so a diagonal back-out is exact too.

import { polygonArea, polygonPerimeter, yIntervalsAt } from "./takeoffGeometry";

const EPS = 1e-9;
// A deduct part outside the room / an overlap smaller than this (SF) is
// rounding noise from a snapped trace, not a finding.
export const NET_AREA_TOLERANCE_SF = 0.01;

export const isRing = (pts) =>
  Array.isArray(pts) && pts.length >= 3 && pts.every(p => Number.isFinite(+p?.x) && Number.isFinite(+p?.y));

// The ring stored on a drawn row's GeometryJson ({ points: [...] }), else null.
export function ringOfGeometry(geometryJson) {
  if (!geometryJson) return null;
  try {
    const g = typeof geometryJson === "string" ? JSON.parse(geometryJson) : geometryJson;
    return isRing(g?.points) ? g.points.map(p => ({ x: +p.x, y: +p.y })) : null;
  } catch {
    return null;
  }
}

function edgesOf(pts) {
  const out = [];
  for (let i = 0; i < pts.length; i++) out.push([pts[i], pts[(i + 1) % pts.length]]);
  return out;
}

// x of the crossing of segments a1→a2 and b1→b2 (inclusive of endpoints),
// else null. Parallel / collinear pairs contribute nothing — their
// endpoints are vertices and already slab boundaries.
function crossingX(a1, a2, b1, b2) {
  const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (Math.abs(d) < EPS) return null;
  const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
  const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / d;
  if (t < -EPS || t > 1 + EPS || u < -EPS || u > 1 + EPS) return null;
  return a1.x + t * (a2.x - a1.x);
}

// Sorted slab boundaries for a set of rings: every vertex x plus every
// pairwise edge-crossing x (within one ring and across rings).
export function slabBoundaries(rings) {
  const xs = new Set();
  const edges = [];
  for (const r of rings) {
    for (const p of r) xs.add(p.x);
    edges.push(...edgesOf(r));
  }
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const x = crossingX(edges[i][0], edges[i][1], edges[j][0], edges[j][1]);
      if (x != null) xs.add(x);
    }
  }
  return [...xs].sort((a, b) => a - b);
}

function mergeIntervals(list) {
  const s = list.filter(([a, b]) => b - a > EPS).sort((p, q) => p[0] - q[0]);
  const out = [];
  for (const [a, b] of s) {
    const last = out[out.length - 1];
    if (last && a <= last[1] + EPS) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

function intersectIntervals(a, b) {
  const out = [];
  for (const [a1, a2] of a) for (const [b1, b2] of b) {
    const lo = Math.max(a1, b1), hi = Math.min(a2, b2);
    if (hi - lo > EPS) out.push([lo, hi]);
  }
  return out;
}

const intervalsLength = (list) => list.reduce((s, [a, b]) => s + (b - a), 0);

// One sweep: area of  outer ∩ (∪ deducts)  and of each  deduct ∩ outer.
export function clippedDeductionAreas(outer, deducts) {
  const xs = slabBoundaries([outer, ...deducts]);
  let unionInside = 0;
  const eachInside = deducts.map(() => 0);
  for (let i = 0; i + 1 < xs.length; i++) {
    const w = xs[i + 1] - xs[i];
    if (w < EPS) continue;
    const xm = (xs[i] + xs[i + 1]) / 2;
    const io = yIntervalsAt(outer, xm);
    if (!io.length) continue;
    const per = deducts.map(d => intersectIntervals(io, yIntervalsAt(d, xm)));
    per.forEach((iv, k) => { eachInside[k] += intervalsLength(iv) * w; });
    unionInside += intervalsLength(mergeIntervals(per.flat())) * w;
  }
  return { unionInside, eachInside };
}

// Metrics of a polygon with holes (holes assumed inside and disjoint — the
// stored shape of a future net region). Compatibility audit §16.
export function polygonWithHolesMetrics({ outer, holes = [] }) {
  const grossArea = isRing(outer) ? polygonArea(outer) : 0;
  const rings = holes.filter(isRing);
  const deductedArea = rings.reduce((s, r) => s + polygonArea(r), 0);
  const outerPerimeter = isRing(outer) ? polygonPerimeter(outer) : 0;
  const holePerimeter = rings.reduce((s, r) => s + polygonPerimeter(r), 0);
  return {
    grossArea,
    deductedArea,
    netArea: Math.max(0, grossArea - deductedArea),
    outerPerimeter,
    holePerimeter,
    totalBoundary: outerPerimeter + holePerimeter,
  };
}

const fmtSf = (v) => (Math.round(v * 10) / 10).toLocaleString();

// Net scope area of one outer polygon less its back-outs.
//   outer    - ring (world feet) — a room's polygonOf() / rectangle, or any
//              scope region
//   deducts  - [{ id?, label?, points }] or bare rings; order is preserved in
//              the returned deductions (index = position in `deducts`)
// Returns { ok, gross, deductions: [{ index, id, label, area, insideArea,
//           outsideArea, clipped, skipped }], totalDeduction (union, inside
//           only), overlapArea, calculatedNet, warnings: [{ code, message,
//           index?, id? }] }
export function netScopeArea({ outer, deducts = [] }) {
  const warnings = [];
  if (!isRing(outer)) {
    return { ok: false, gross: 0, deductions: [], totalDeduction: 0, overlapArea: 0, calculatedNet: 0,
      warnings: [{ code: "outer_invalid", message: "The scope outline has fewer than three points — nothing to deduct from." }] };
  }
  const gross = polygonArea(outer);
  const deductions = new Array(deducts.length);
  const valid = [];
  deducts.forEach((d, index) => {
    const pts = Array.isArray(d) ? d : d?.points;
    const id = Array.isArray(d) ? null : (d?.id ?? null);
    const label = (Array.isArray(d) ? null : d?.label) || (id != null ? `#${id}` : `${index + 1}`);
    const area = isRing(pts) ? polygonArea(pts) : 0;
    if (!(area > EPS)) {
      warnings.push({ code: "deduct_degenerate", index, id, message: `Back-out ${label} has no area and was skipped.` });
      deductions[index] = { index, id, label, area: 0, insideArea: 0, outsideArea: 0, clipped: false, skipped: true };
      return;
    }
    valid.push({ index, id, label, pts, area });
  });

  const { unionInside, eachInside } = clippedDeductionAreas(outer, valid.map(v => v.pts));
  let sumInside = 0;
  valid.forEach((v, k) => {
    const insideArea = Math.min(v.area, Math.max(0, eachInside[k]));
    const outsideArea = Math.max(0, v.area - insideArea);
    const clipped = outsideArea > NET_AREA_TOLERANCE_SF;
    if (clipped) {
      warnings.push({ code: "deduct_outside_room", index: v.index, id: v.id,
        message: `${fmtSf(outsideArea)} SF of back-out ${v.label} lies outside the scope outline and is not deducted.` });
    }
    sumInside += insideArea;
    deductions[v.index] = { index: v.index, id: v.id, label: v.label, area: v.area, insideArea, outsideArea, clipped, skipped: false };
  });

  const totalDeduction = Math.min(gross, Math.max(0, unionInside));
  const overlapArea = Math.max(0, sumInside - totalDeduction);
  if (overlapArea > NET_AREA_TOLERANCE_SF) {
    warnings.push({ code: "deducts_overlap", message: `Back-outs overlap by ${fmtSf(overlapArea)} SF — the overlap is deducted once.` });
  }
  const calculatedNet = Math.max(0, gross - totalDeduction);
  return { ok: true, gross, deductions, totalDeduction, overlapArea, calculatedNet, warnings };
}

// ── Back-out rows (Track D-2) ─────────────────────────────────────────────
// A manual back-out is a TakeoffConditionsDrawn row with MeasureRole =
// 'deduct' and AppliesToRoomElementID = the Room Element whose area it is
// NOT applied to (db/37 columns, FK in db/70). No catalog entity — it prices
// nothing itself; the element's resolver subtracts it. Legacy rows (NULL
// MeasureRole) are ordinary additive takeoff.
export const BACK_OUT_ROLE = "deduct";
export const isBackOut = (row) => row?.MeasureRole === BACK_OUT_ROLE;
// Area bases a back-out deducts from (roomElements.js ElementQuantityBasis).
// Wall area waits for opening deductions (D-3) — a floor polygon says nothing
// about a wall.
export const BACK_OUT_BASES = new Set(["floor_area", "ceiling_area"]);

// The back-outs drawn against one element, shaped for netScopeArea().
export function backOutsForElement(element, conditions = []) {
  const id = element?.RoomElementID;
  if (id == null || !Array.isArray(conditions)) return [];
  return conditions
    .filter(c => isBackOut(c) && c.AppliesToRoomElementID === id)
    .map(c => ({ id: c.TakeoffConditionDrawnID, label: c.Notes || `Back-out #${c.TakeoffConditionDrawnID}`, points: ringOfGeometry(c.GeometryJson), row: c }));
}
